import {
  Button,
  IconButton,
  Input,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
} from '@material-ui/core';
import clsx from 'clsx';
import {
  checkboxSelected,
  checkboxUnselected,
} from 'components/SelectIconComponent';
import { useCallback, useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { CustomSelectOption, CustomSelectProps } from './__types';
import { useCustomMultipleSelect } from './style';
import { useRotateAnimation } from 'components/commons';
import CloseIcon from 'components/Icons/CloseIcon';
import RefreshIcon from 'components/Icons/RefreshIcon';
import DefaultArrowSelect from 'assets/icons/DefaultArrowSelect';
import CloseIconEditEscola from 'assets/icons/CloseIconEditEscola';

function CustomMultipleSelect({
  options,
  label,
  value,
  placeholder,
  multiple,
  menuMaxHeight,
  error,
  helperText,
  onChange,
  onFocus,
  onBlur,
  disableFilter,
  loading,
  disabled,
}: CustomSelectProps) {
  const classes = useCustomMultipleSelect();
  const rotate = useRotateAnimation();
  const [currentValue, setValue] = useState<any>(value);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState<string>();
  const [, setLabelColor] = useState('initial');
  const textFieldRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [, setSelectedOptions] = useState<any[]>([]);
  const [sortedOptions, setSortedOptions] = useState(options);

  const renderValue = useMemo(() => {
    if (multiple && currentValue && Array.isArray(currentValue)) {
      const itemCount = currentValue.length;
      const selectedLabel = itemCount > 1 ? 'selecionados' : 'selecionado';
      return itemCount !== 0 ? `${itemCount} ${selectedLabel}` : '';
    }
    if (currentValue !== undefined) {
      const current = options.find((option) => option.value === currentValue);
      return current?.selected || current?.label || '';
    }
    return '';
  }, [currentValue, multiple, options]);

  const handleSelectMultiple = useCallback(
    (option: CustomSelectOption) => {
      const isSelected = currentValue.includes(option.value);
      let updatedCurrentValue = [...currentValue];
      if (!isSelected) {
        updatedCurrentValue.push(option.value);
      } else {
        updatedCurrentValue = updatedCurrentValue.filter((optionValue) => optionValue !== option.value);
      }
      setValue(updatedCurrentValue);
      setSelectedOptions(updatedCurrentValue);
      onChange(updatedCurrentValue);
    },
    [currentValue, onChange]
  );

  useEffect(() => {
    if (value && Array.isArray(value) && value.length === 0) {
      setValue(undefined);
    } else {
      setSelectedOptions(value || []);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (textFieldRef.current && !textFieldRef.current.contains(e.target as Node)) {
        setLabelColor(isMenuOpen ? '#47d6ff' : '#000');
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleBlur = () => {
    if (disabled) return;
    setLabelColor(isMenuOpen ? '#47d6ff' : '#000');
  };

  useEffect(() => {
    setValue(value);
  }, [value]);

  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (multiple && currentValue && currentValue.length > 0) {
        const selectedOptions = options.filter((option) =>
          currentValue.includes(option.value)
        );
        const unselectedOptions = options.filter(
          (option) => !currentValue.includes(option.value)
        );
        if (isMounted.current) {
          setSortedOptions([...selectedOptions, ...unselectedOptions]);
        }
      } else {
        if (isMounted.current) {
          setSortedOptions(options);
        }
      }
    }, 1000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [options, currentValue, multiple]);

  let labelColor;
  if (disabled) {
    labelColor = '#9E9E9E';
  } else if (error) {
    labelColor = '#f79596';
  } else {
    labelColor = 'inherit';
  }
  const isLabelShrunk = Boolean(placeholder) || Boolean(currentValue);
  const helperTextColor = error ? '#f79596' : 'inherit';

  function useBreakpoint(): 'mobile' | 'tablet' | 'desktop' {
    const [width, setWidth] = useState(window.innerWidth);

    useEffect(() => {
      const handleResize = () => setWidth(window.innerWidth);
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (width < 600) return 'mobile';
    if (width < 1100) return 'tablet';
    return 'desktop';
  }

  const breakpoint = useBreakpoint();

  const menuHeightDinamic = useMemo(() => {
    if (!menuMaxHeight) return undefined;
    return (
      menuMaxHeight[breakpoint] ??
      undefined
    );
  }, [menuMaxHeight, breakpoint]);


  return (
    <Fragment>
      <TextField
        label={label}
        ref={textFieldRef}
        placeholder={placeholder}
        value={renderValue}
        onBlur={handleBlur}
        InputProps={{
          onClick: (e) => {
            if (disabled) return;
            setAnchor(e.currentTarget);
            onFocus && onFocus();
            setMenuOpen(true);
          },
          className: clsx(classes.input, {
            [classes.loading]: loading,
          }),
          endAdornment: (
            <InputAdornment position='end' className={classes.adornment}>
              {renderValue && !disabled && (
                <IconButton
                  type='button'
                  color='primary'
                  onClick={(e) => {
                    e.stopPropagation();
                    setValue([]);
                    onChange([]);
                    onBlur && onBlur();
                  }}
                  className={classes.adornmentButton}
                  disableRipple
                  disabled={disabled}
                  data-testid='CloseButton'
                >
                  <CloseIconEditEscola />
                </IconButton>
              )}
              <IconButton
                type='button'
                color='primary'
                className={classes.adornmentButton}
                disabled={disabled || loading}
                disableRipple
              >
                {loading ? (
                  <RefreshIcon className={rotate.complete} />
                ) : (
                  <DefaultArrowSelect
                    color={anchor ? '#7f7f7f' : '#CfCfCf'}
                    className={clsx(classes.adornmentIcon, {
                      [classes.adornmentIconRotate]: anchor,
                    })}
                  />
                )}
              </IconButton>
            </InputAdornment>
          ),
          readOnly: true,
        }}
        InputLabelProps={{
          shrink: isLabelShrunk,
          style: { color: labelColor },
        }}
        fullWidth
        error={error}
        helperText={
          <span style={{ color: helperTextColor }}>
            {helperText}
          </span>
        }
        disabled={disabled}
      />
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setFilter(undefined);
          onBlur && onBlur();
        }}
        PaperProps={{
          className: classes.menu,
          style: {
            width: anchor?.getBoundingClientRect().width,
            marginTop: (anchor?.getBoundingClientRect().height || 0) + 8,
            maxHeight: menuHeightDinamic || '',
          },
        }}
        MenuListProps={{
          style: {
            paddingTop: !disableFilter ? 0 : 8,
            paddingBottom: multiple ? 0 : 8,
          },
        }}
        variant='menu'
      >
        {!disableFilter && (
          <MenuItem
            className={classes.menuItemFilter}
            button={false}
            onKeyDown={(e) => e.stopPropagation()}
            onFocus={(e) => e.currentTarget.querySelector('input')?.focus()}
          >
            <Input
              placeholder='Pesquisar'
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              endAdornment={
                filter ? (
                  <InputAdornment position='end'>
                    <IconButton
                      color='primary'
                      onClick={() => setFilter('')}
                      data-testid='closeFilter'
                    >
                      <CloseIcon style={{ fontSize: 20 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }
              autoFocus
              fullWidth
            />
          </MenuItem>
        )}

        {sortedOptions
          .filter((option) => {
            if (filter)
              return option.label.toLowerCase().includes(filter.toLowerCase());
            return true;
          })
          .map((option) => {
            const isSelected = currentValue.includes(option.value);
            return (
              <MenuItem
                key={option.value}
                className={classes.menuItem}
                onClick={() => {
                  handleSelectMultiple(option);
                }}
                selected={isSelected}
              >
                {multiple && (
                  <ListItemIcon className={classes.menuItemCheckbox}>
                    {isSelected
                      ? checkboxSelected('small')
                      : checkboxUnselected('small')}
                  </ListItemIcon>
                )}
                {option.label}
              </MenuItem>
            );
          })}
        {multiple && (
          <MenuItem className={classes.menuItemActions} button={false}>
            <Button
              className={classes.btnTextSelectedAll}
              type='button'
              variant='text'
              color='primary'
              onClick={() => {
                const newValue = options.map((option) => option.value);
                setValue(newValue);
                onChange(newValue);
              }}
              disabled={currentValue && currentValue.length === options.length}
            >
              Selecionar tudo
            </Button>
            <Button
              className={classes.btnTextRemoveAll}
              type='button'
              variant='text'
              color='primary'
              onClick={() => {
                setValue([]);
                setSelectedOptions([]);
                onChange([]);
              }}
              disabled={!currentValue || !currentValue.length}
            >
              Limpar tudo
            </Button>
          </MenuItem>
        )}
      </Menu>
    </Fragment>
  );
}

export default CustomMultipleSelect;
